import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface TypedLogoProps {
  showAnimation?: boolean;
}

export function TypedLogo({ showAnimation = false }: TypedLogoProps) {
  const { user } = useAuth();
  const [text, setText] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  const handleLogoClick = () => {
    if (!isHomePage && !user) {
      navigate('/');
    }
  };

  const fullText = 'Techfolio';
  const typingSpeedRef = useRef(150);
  const currentIndexRef = useRef(0);
  const animationTimeoutId = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!showAnimation) {
      setText(fullText);
      return;
    }

    currentIndexRef.current = 0;
    setText('');

    const typeNextCharacter = () => {
      if (currentIndexRef.current < fullText.length) {
        setText(fullText.slice(0, currentIndexRef.current + 1));
        currentIndexRef.current++;
        typingSpeedRef.current = Math.random() * 100 + 150;
        animationTimeoutId.current = setTimeout(typeNextCharacter, typingSpeedRef.current);
      }
    };
    animationTimeoutId.current = setTimeout(typeNextCharacter, 1000);
    return () => {
      if (animationTimeoutId.current) {
        clearTimeout(animationTimeoutId.current);
      }
    };
  }, [showAnimation]);

  const LogoContent = (
    <>
      <div className="relative">
       <div className="text-lg md:text-2xl font-thin tracking-tight text-white">
          <span className="inline-block">
            {text}
          </span>
          <span className="cursor-blink inline-block w-[1ch]">
            _
          </span>
        </div>
        <div className="absolute top-full left-0 w-full h-4">
          <div className="absolute -left-2 top-0 bg-gradient-to-r from-transparent via-white to-transparent h-[2px] blur-sm"
            style={{
              width: text.length < fullText.length ? '0%' : '100%',
              transition: text.length < fullText.length ? 'none' : 'width 0.15s ease-out'
            }} />
          <div className="absolute -left-2 top-0 bg-gradient-to-r from-transparent via-white to-transparent h-px"
            style={{
              width: text.length < fullText.length ? '0%' : '100%',
              transition: text.length < fullText.length ? 'none' : 'width 0.15s ease-out'
            }} />
          <div className="absolute left-0 top-0 bg-gradient-to-r from-transparent via-white to-transparent h-[3px] blur-sm"
            style={{
              width: text.length < fullText.length ? '0%' : '40%',
              marginLeft: text.length < fullText.length ? '0%' : '30%',
              transition: text.length < fullText.length ? 'none' : 'all 0.1s ease-in'
            }} />
          <div className="absolute left-0 top-0 bg-gradient-to-r from-transparent via-white to-transparent h-px"
            style={{
              width: text.length < fullText.length ? '0%' : '40%',
              marginLeft: text.length < fullText.length ? '0%' : '30%',
              transition: text.length < fullText.length ? 'none' : 'all 0.1s ease-in'
            }} />
        </div>
      </div>
    </>
  );

return (
  <div
    onClick={handleLogoClick}
    className="flex items-center space-x-0 group relative cursor-pointer hover:opacity-90 transition-opacity"
    role="button"
    tabIndex={0}
    onKeyDown={(e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        handleLogoClick();
      }
    }}
    aria-label="Navigate to homepage"
  >
    {LogoContent}
  </div>
);
}